import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/features/auth/use-auth";
import { cn } from "@/lib/utils";
import {
	type DeviceAgent,
	getConnectionSecret,
	getRecentSessionForTarget,
	mintLiteConnectToken,
	recordRecentConnection,
} from "./devices-api";
import {
	buildRustdeskDirectLink,
	canConnectOfflineDirect,
	isDeviceReachable,
	isWindowsClient,
} from "./devices-helpers";
import { RECENT_CONNECTIONS_QUERY_KEY } from "./use-devices-query";

/** Scheme registered by the FULL device-agent installer + helper (HKLM + HKCU). */
const AGENT_PROTOCOL = "farmaster-agent://connect/";

/** How long to wait for the full agent to pick up the deeplink before falling
 *  back to Lite, and how often to probe the backend in that window. */
const FALLBACK_AFTER_MS = 3000;
const PROBE_INTERVAL_MS = 600;

interface ConnectButtonProps {
	device: DeviceAgent;
	/** Dodatkowe klasy (np. szerokosc na telefonie); desktop bez zmian. */
	className?: string;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Did the FULL agent pick up the click? It writes a remote-session row server
 * side; the Lite path writes none. Poll for ~3s — true as soon as a session
 * appears. Network hiccups during polling are ignored (keep trying).
 */
async function fullAgentHandled(deviceUuid: string): Promise<boolean> {
	const deadline = Date.now() + FALLBACK_AFTER_MS;
	while (Date.now() < deadline) {
		await sleep(PROBE_INTERVAL_MS);
		try {
			const { initiated } = await getRecentSessionForTarget(deviceUuid);
			if (initiated) return true;
		} catch {
			// ignore — transient; the deadline still bounds the wait.
		}
	}
	return false;
}

/**
 * „Połącz" — JEDEN przycisk, automatyczny dla KAŻDEGO komputera operatora.
 *
 * Wszyscy używają pełnego agenta (schemat `farmaster-agent://`); FarmasterAgent
 * Lite (schemat `farmaster://` + token) jest opcją dla prywatnych komputerów bez
 * pełnego agenta. Przeglądarka nie wykryje, co jest zainstalowane — więc:
 *
 *   1. otwórz `farmaster-agent://connect/<uuid>` (pełny agent — domyślny),
 *   2. pytaj backend, czy powstała sesja (pełny agent tworzy wiersz; Lite nie),
 *   3. jest w ≤3s → pełny agent złapał, koniec,
 *   4. brak po 3s → mint token → `farmaster://connect/<token>` (Lite).
 *
 * Wyjątek: super_admin na Macu/Linuksie (agent i Lite są tylko na Windows) —
 * pobiera ID + hasło (endpoint reveal, audytowany) i otwiera stockowy RustDesk
 * linkiem `rustdesk://`, bez agenta i bez schematów `farmaster*://`.
 *
 * Podczas całego flow przycisk jest w stanie ładowania (spinner + disabled), żeby
 * operator nie klikał wielokrotnie i nie otwierał kilku sesji naraz.
 */
export function ConnectButton({ device, className }: ConnectButtonProps) {
	const [connecting, setConnecting] = useState(false);
	const queryClient = useQueryClient();
	// Synchronous re-entry guard: catches rapid double-clicks BEFORE React has a
	// chance to re-render the button disabled (the `connecting` state lags a tick).
	const inFlight = useRef(false);

	// Gate on live hub presence — the same truth the backend session gate uses —
	// so the button is never enabled for a device that would 409 on connect.
	const { hasAnyRole } = useAuth();
	const offline = !isDeviceReachable(device);
	// super_admin: cel offline z polityką unattended wydzwaniamy bezpośrednio
	// przez FarmasterRustDesk (stałe ID+hasło), z pominięciem agenta.
	const isSuperAdmin = hasAnyRole(["super_admin"]);
	const offlineDirect = canConnectOfflineDirect(device, isSuperAdmin);
	const disabled = (offline && !offlineDirect) || connecting;
	const disabledHint = offline
		? offlineDirect
			? "Agent offline — połączenie bezpośrednie przez FarmasterRustDesk (super_admin)."
			: "Urządzenie jest offline — nie można się połączyć."
		: undefined;

	/** Mac/Linux (super_admin): stockowy RustDesk przez `rustdesk://`, bez agenta. */
	async function openStockRustdesk() {
		const secret = await getConnectionSecret(device.deviceUuid);
		const rustdeskId = secret.rustdeskId ?? device.rustdeskId;
		if (!rustdeskId) {
			toast.error("Urządzenie nie zgłosiło jeszcze ID RustDesk.");
			return;
		}
		const link = buildRustdeskDirectLink(rustdeskId, secret.unattendedPassword);
		const open = () => {
			window.location.href = link;
		};
		open();
		toast.message("Otwieram RustDesk…", {
			description: secret.unattendedPassword
				? "Jeśli nic się nie dzieje, zainstaluj RustDesk ze strony rustdesk.com."
				: "Osoba przy stanowisku musi zaakceptować połączenie. Jeśli nic się nie dzieje, zainstaluj RustDesk ze strony rustdesk.com.",
			// Safari może zablokować otwarcie aplikacji po zapytaniu do API —
			// ponowne kliknięcie to nowy gest użytkownika.
			action: { label: "Otwórz ponownie", onClick: open },
		});
	}

	async function handleConnect(e: React.MouseEvent) {
		e.stopPropagation();
		if (inFlight.current) return; // ignore re-clicks while a connect is in flight
		inFlight.current = true;

		setConnecting(true);
		// Zapisz w historii „Ostatnie połączenia" (per-user) — fire-and-forget:
		// najlepszy wysiłek, NIGDY nie blokuje ani nie psuje samego połączenia. Po
		// zapisie invaliduj query historii, żeby baner odświeżył się OD RAZU (bez
		// czekania na przypadkowy refetch przy odzyskaniu fokusu okna).
		void recordRecentConnection(device.deviceUuid)
			.then(() => queryClient.invalidateQueries({ queryKey: RECENT_CONNECTIONS_QUERY_KEY }))
			.catch(() => {});
		try {
			if (isSuperAdmin && !isWindowsClient()) {
				await openStockRustdesk();
				return;
			}

			// 1. Pełny agent (domyślny). Otwarcie protokołu NIE przeładowuje strony.
			window.location.href = `${AGENT_PROTOCOL}${encodeURIComponent(device.deviceUuid)}`;
			toast.message(
				offlineDirect
					? "Agent offline — łączę bezpośrednio przez FarmasterRustDesk…"
					: "Otwieram zdalny pulpit na tym komputerze…",
			);

			// 2-3. Złapał pełny agent? (sesja po stronie backendu)
			if (await fullAgentHandled(device.deviceUuid)) return;

			// Ścieżka ratunkowa (cel offline) idzie WYŁĄCZNIE przez pełny agent:
			// Lite nie zna wiersza urządzenia i tylko maskowałby brak sesji.
			if (offlineDirect) {
				toast.error("Backend nie utworzył sesji offline.", {
					description:
						"Sprawdź, czy FarmasterAgent na tym komputerze działa (ikona w trayu) i czy stanowisko ma politykę „bez potwierdzenia”.",
				});
				return;
			}

			// 4. Brak agenta → fallback na FarmasterAgent Lite.
			const { deeplink } = await mintLiteConnectToken(device.deviceUuid);
			window.location.href = deeplink;
			toast.message("Łączę przez FarmasterAgent Lite…", {
				description:
					"Jeśli nic się nie dzieje, zainstaluj FarmasterAgent (pełny lub Lite) — przycisk „Instalator”.",
			});
		} catch {
			toast.error("Nie udało się przygotować połączenia. Spróbuj ponownie.");
		} finally {
			inFlight.current = false;
			setConnecting(false);
		}
	}

	return (
		<Button
			size="sm"
			variant="outline"
			// Telefon: glowna akcja kafla/wiersza — cel dotyku 44 px.
			className={cn("max-md:h-11", className)}
			disabled={disabled}
			title={disabledHint}
			onClick={handleConnect}
		>
			{connecting ? (
				<>
					<Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
					Łączę…
				</>
			) : offlineDirect ? (
				"Połącz (offline)"
			) : (
				"Połącz"
			)}
		</Button>
	);
}
